import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../axiosConfig';
import { buildMediaUrl } from '../apiConfig';
import { useNavigate } from 'react-router-dom';

function Cart({ refreshToken = 0, onOrderComplete, updateCartItemsCount }) {
    const [cartItems, setCartItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);        // для кнопки "Заказать"
    const [isClearing, setIsClearing] = useState(false);      // для "Очистить корзину"
    const [removingIds, setRemovingIds] = useState(new Set()); // для кнопок удаления по позиции
    const [updatingQuantityIds, setUpdatingQuantityIds] = useState(new Set());
    const [requiresRtcsdTicket, setRequiresRtcsdTicket] = useState(false);
    const [rtcsdIsCassette, setRtcsdIsCassette] = useState(false);
    const [rtcsdTicketNumber, setRtcsdTicketNumber] = useState('');
    const [rtcsdDeviceSerial, setRtcsdDeviceSerial] = useState('');
    const navigate = useNavigate();

    const safeErr = (err, fallback) =>
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        fallback;

    const toast = (text, danger=false) => {
        if (!window.Toastify) return;
        window.Toastify({
            text,
            backgroundColor: danger
                ? "linear-gradient(to right, #ff5f6d, #ffc371)"
                : "linear-gradient(to right, #28a745, #58d18b)",
            duration: 5000
        }).showToast();
    };

    const isHyosungProject = (projectName) => {
        const normalized = String(projectName || '').trim().toLowerCase().replace(/[^a-z0-9а-яё]+/g, '');
        return normalized.includes('hyosung');
    };

    const fetchCart = useCallback(() => {
        axiosInstance.get('/api/cart/')
            .then(response => {
                const sortedItems = (response.data.items || []).sort((a, b) => a.component_id - b.component_id);
                setCartItems(sortedItems);
                const hasHyosungItems = sortedItems.some(item =>
                    item.is_hyosung_project || isHyosungProject(item.project)
                );
                const shouldRequireRtcsdTicket = Boolean(response.data.requires_rtcsd_ticket || hasHyosungItems);
                setRequiresRtcsdTicket(shouldRequireRtcsdTicket);
                setRtcsdIsCassette(Boolean(response.data.rtcsd_is_cassette));
                setRtcsdTicketNumber(response.data.rtcsd_ticket_number || '');
                setRtcsdDeviceSerial(response.data.rtcsd_device_serial || '');
                if (!shouldRequireRtcsdTicket) {
                    setRtcsdIsCassette(false);
                    setRtcsdTicketNumber('');
                    setRtcsdDeviceSerial('');
                }
            })
            .catch(error => {
                console.error('Error fetching cart:', error);
                toast('Не удалось загрузить корзину', true);
            });
    }, []);

    useEffect(() => {
        fetchCart();
    }, [fetchCart, refreshToken]);

    const handleCompleteOrder = () => {
        if (isLoading || isClearing || removingIds.size > 0 || updatingQuantityIds.size > 0) return;
        if (requiresRtcsdTicket && !rtcsdIsCassette && !rtcsdTicketNumber.trim()) {
            toast('Для проекта Hyosung укажите номер заявки RT CSD', true);
            return;
        }
        setIsLoading(true);

        axiosInstance.post('/api/complete-order/', {
            rtcsd_is_cassette: rtcsdIsCassette,
            rtcsd_ticket_number: rtcsdIsCassette ? '' : rtcsdTicketNumber.trim(),
            rtcsd_device_serial: rtcsdIsCassette ? '' : rtcsdDeviceSerial.trim(),
        })
            .then(response => {
                if (response.data.success) {
                    fetchCart();
                    onOrderComplete && onOrderComplete();
                    updateCartItemsCount && updateCartItemsCount();
                } else {
                    console.warn('Order completion error:', response.data.error);
                    toast(response.data.error || 'Ошибка при заказе', true);
                }
            })
            .catch(error => {
                console.error('Error completing order:', error);
                toast(safeErr(error, 'Произошла ошибка при заказе.'), true);
            })
            .finally(() => setIsLoading(false));
    };

    const handleItemClick = (componentId) => {
        navigate(`/component/${componentId}`);
    };

    const handleRemoveItem = (componentId) => {
        if (removingIds.has(componentId) || updatingQuantityIds.has(componentId) || isClearing || isLoading) return;

        setRemovingIds(prev => new Set(prev).add(componentId));
        axiosInstance.post('/api/remove_from_cart/', { component_id: componentId })
            .then(response => {
                fetchCart();
                updateCartItemsCount && updateCartItemsCount();
            })
            .catch(error => {
                console.error('Error removing item from cart:', error);
                toast(safeErr(error, 'Не удалось удалить товар из корзины'), true);
                // Обновим состояние корзины на всякий случай
                fetchCart();
            })
            .finally(() => {
                setRemovingIds(prev => {
                    const next = new Set(prev);
                    next.delete(componentId);
                    return next;
                });
            });
    };

    const handleClearCart = () => {
        if (isClearing || isLoading || removingIds.size > 0 || updatingQuantityIds.size > 0) return;

        setIsClearing(true);
        axiosInstance.post('/api/clear_cart/')
            .then(response => {
                fetchCart();
                updateCartItemsCount && updateCartItemsCount();
            })
            .catch(error => {
                console.error('Error clearing cart:', error);
                toast(safeErr(error, 'Не удалось очистить корзину'), true);
                // Обновим корзину, если сервер частично что-то изменил
                fetchCart();
            })
            .finally(() => setIsClearing(false));
    };

    const handleQuantityChange = (componentId, newQuantity) => {
        if (isLoading || isClearing || removingIds.has(componentId) || updatingQuantityIds.has(componentId)) return;
        const q = parseInt(newQuantity, 10);
        if (!q || q < 1) return;

        setUpdatingQuantityIds(prev => new Set(prev).add(componentId));
        axiosInstance.post('/api/update_cart_item_quantity/', { component_id: componentId, quantity: q })
            .then(response => {
                if (response.data.success) {
                    setCartItems(prevItems =>
                        prevItems.map(item =>
                            item.component_id === componentId ? { ...item, quantity: q } : item
                        )
                    );
                } else {
                    toast(response.data.error || 'Ошибка при изменении количества', true);
                    if (response.data.max_quantity !== undefined) {
                        setCartItems(prevItems => prevItems.map(item =>
                            item.component_id === componentId ? { ...item, quantity: response.data.max_quantity } : item
                        ));
                    }
                }
            })
            .catch(error => {
                console.error('Error updating quantity:', error);
                toast(safeErr(error, 'Произошла ошибка при обновлении количества.'), true);
            })
            .finally(() => {
                setUpdatingQuantityIds(prev => {
                    const next = new Set(prev);
                    next.delete(componentId);
                    return next;
                });
            });
    };

    return (
        <div className="cart container mt-4">
            <h2>Ваша корзина</h2>
            {cartItems.length > 0 ? (
                <>
                    <ul className="list-group">
                        {cartItems.map(item => (
                            <li key={item.component_id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div className="cart-item-info" onClick={() => handleItemClick(item.component_id)} style={{ cursor: 'pointer' }}>
                                    {item.photo && (
                                        <img src={buildMediaUrl(item.photo)} alt={item.component} style={{ width: '50px', marginRight: '10px' }} />
                                    )}
                                    <span>{item.component}</span>
                                    <small>({item.storage_location})</small>
                                    {item.project && (
                                        <small className="cart-item-project">Проект: {item.project}</small>
                                    )}
                                </div>
                                <div className="d-flex delet align-items-center">
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        min="1"
                                        onChange={(e) => handleQuantityChange(item.component_id, e.target.value)}
                                        disabled={isLoading || isClearing || removingIds.has(item.component_id) || updatingQuantityIds.has(item.component_id)}
                                        style={{ width: '60px', marginRight: '10px' }}
                                    />
                                    <span className="mr-2">шт.</span>
                                    <button
                                        className="btn p-0"
                                        onClick={() => handleRemoveItem(item.component_id)}
                                        disabled={isLoading || isClearing || removingIds.has(item.component_id) || updatingQuantityIds.has(item.component_id)}
                                        style={{ border: 'none', background: 'transparent' }}
                                        title={removingIds.has(item.component_id) ? 'Удаление...' : 'Удалить'}
                                    >
                                        <i className="fas fa-times text-danger" style={{ fontSize: '18px', opacity: removingIds.has(item.component_id) ? 0.5 : 1 }}></i>
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                    {requiresRtcsdTicket && (
                        <div className="rtcsd-cart-fields mt-3">
                            <div className="form-check mb-2">
                                <input
                                    id="rtcsd-is-cassette"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={rtcsdIsCassette}
                                    onChange={(event) => {
                                        const checked = event.target.checked;
                                        setRtcsdIsCassette(checked);
                                        if (checked) {
                                            setRtcsdTicketNumber('');
                                            setRtcsdDeviceSerial('');
                                        }
                                    }}
                                    disabled={isLoading || isClearing || removingIds.size > 0 || updatingQuantityIds.size > 0}
                                />
                                <label className="form-check-label" htmlFor="rtcsd-is-cassette">
                                    Кассета
                                </label>
                            </div>
                            {!rtcsdIsCassette && (
                                <>
                                    <div className="form-group mb-2">
                                        <label htmlFor="rtcsd-ticket-number">Номер заявки RT CSD</label>
                                        <input
                                            id="rtcsd-ticket-number"
                                            className="form-control"
                                            value={rtcsdTicketNumber}
                                            onChange={(event) => setRtcsdTicketNumber(event.target.value)}
                                            placeholder="00824504"
                                            disabled={isLoading || isClearing || removingIds.size > 0 || updatingQuantityIds.size > 0}
                                        />
                                    </div>
                                    <div className="form-group mb-0">
                                        <label htmlFor="rtcsd-device-serial">Серийный номер устройства</label>
                                        <input
                                            id="rtcsd-device-serial"
                                            className="form-control"
                                            value={rtcsdDeviceSerial}
                                            onChange={(event) => setRtcsdDeviceSerial(event.target.value)}
                                            placeholder="CGJX238891"
                                            disabled={isLoading || isClearing || removingIds.size > 0 || updatingQuantityIds.size > 0}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <div className="d-flex cart-componetn justify-content-start mt-3">
                        <button className="btn btn-warning mr-2" onClick={handleClearCart} disabled={isClearing || isLoading || removingIds.size > 0 || updatingQuantityIds.size > 0}>
                            {isClearing ? 'Очищаем…' : 'Очистить корзину'}
                        </button>
                        <button
                            className="btn btn-success"
                            onClick={handleCompleteOrder}
                            disabled={isLoading || isClearing || removingIds.size > 0 || updatingQuantityIds.size > 0}
                        >
                            {isLoading ? 'Обработка...' : 'Заказать'}
                        </button>
                    </div>
                </>
            ) : (
                <p>Корзина пуста</p>
            )}
        </div>
    );
}

export default Cart;
